import { AfterContentChecked, Component, ElementRef, HostListener, inject, Inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { faAngleDown, faUser, faUserCog, faQuestionCircle, faBuildingColumns, faBuilding, faClipboardList, faUsers, faVirus, faHouse, faArrowRight, faSwap } from '@fortawesome/pro-solid-svg-icons';
import { SwUpdate } from '@angular/service-worker';
import { filter, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import Rollbar from 'rollbar';
import { RollbarService } from '../rollbar_config';

import { GlobalService } from './shared/service/global.service';
import { SecurityService } from './core/security/security.service';
import { UserJsVm } from './shared/generated/Administration/Models/User/JavaScript/UserJsVm';
import { pcgSettings } from './shared/generated/pcg-settings';
import { NavigationService } from './shared/navigation/navigation.service';
import { SystemMessageService } from '../app/core/system-message/system-message-service';
import { GlobalVariablesService } from './services/global-variables.service'
import { PermissionProfile } from './core/enums/generated/PermissionProfile';
import { SelectItem } from './shared/models/select-item';
import { SwapEventVm } from './shared/generated/Administration/Models/User/SwapEventVm';
import { MouAckComponent } from './shared/navigation/mou-ack/mou-ack.component';

/**
 * This is the application's root component.
 */
@Component({
    selector: 'pcg-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [GlobalVariablesService],
    standalone: false
})
export class AppComponent implements OnInit, AfterContentChecked, OnDestroy {

	@ViewChild('tabPanel') tabPanel: any;
    @ViewChild('modalChangeEvent', { static: true }) modalChangeEvent: TemplateRef<any>;

	updateCheckText = '';
    mode = pcgSettings.mode; //for responsive BETA text

    isLoggedIn = signal<boolean>(false); 
    isLoading = signal<boolean>(false); 

	errorCache: any = {};

	// The following are error codes thrown as a result of system security
	// and should not be logged to rollbar.
	rollbarIgnoredCodes: number[] = [
		0, 		// Network Error / Aborted Request
		401, 	// Login redirect
		403, 	// Access denied
		467,	// 2fa not inabled
		503, 	// Service Unavailable
		504 	// Gateway timeout
	];

	// My Profile
	user: UserJsVm;

	// Fa icons
	faIconName = { 
		faAngleDown
		, faUser
		, faUserCog
		, faQuestionCircle
		, faBuildingColumns
		, faBuilding
		, faClipboardList
		, faUsers
		, faVirus
        , faHouse
        , faArrowRight
        , faSwap
	};

	// For nav selected items
	sitesSelected: boolean;
	orgsSelected: boolean;
	usersSelected: boolean;
	reportsSelected: boolean;
	adminSelected: boolean;
	helpSelected: boolean;
	userProfileSelected: boolean;

    isMobile: boolean;
    onChangePasswordScreen: boolean;
	canManageUsers = false;
	isAdmin = false;
	isOrganizationAdmin = false;
	isSiteUser = false;
	isSiteManager = false;

    eventItems: SelectItem[] = [];
    formGroup = SwapEventVm.Form;
    model = SwapEventVm.GetModel(this.formGroup);	
    currentEventId = 0;
    eventName: string = "";
    isAckOpen = false;

    navService = inject(NavigationService);
    private rollbar = inject(RollbarService);

	// Inactivity timeout needs to be injected here to have a constant timeout going in the background
	constructor(
		private swUpdate: SwUpdate
		, public router: Router
		, private sec: SecurityService
		, private modalConfig: NgbModalConfig
		, private elRef: ElementRef
        , private modalService: NgbModal
		, private ms: SystemMessageService
		, public globalService: GlobalService
		, private gvs: GlobalVariablesService
        , private httpClient: HttpClient
	) {
		this.configureRollbar(); // Set global rollbar settings
		this.configureModal(); // Set default modal settings		
		this.setIsLoggedInHtmlClass(); // Handle is-logged-in HTML class

		// Dirty AngularJS style global router used in index.html
		window['$router'] = router;
	}

    @HostListener('window:resize')
	onResize() { 
		this.isMobile = GlobalService.setIsMobile(window.innerWidth); 
		if (this.isMobile) {
			localStorage.setItem("isNavFixed", "false"); 
			this.navService.toggleNavFixed(false);
			this.navService.toggleNavOpen(false);
		}
	}

	ngOnInit() { 
        this.router.events.pipe(
            filter((e) =>
                e instanceof NavigationStart 
                || e instanceof NavigationEnd 
                || e instanceof NavigationCancel 
                || e instanceof NavigationError
            )
            , map((e) => e instanceof NavigationStart)
        ).subscribe(e => { this.isLoading.set(e); });
        
		this.navService.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
        // Check isNavFixed global variable on init for pages without sidenav
		if (this.gvs.isNavFixed()) {
			this.navService.isNavFixed.set(true);
			this.navService.isNavOpen.set(true);			
		}
		this.sec.user$.subscribe(user => { 
			this.user = user;
		}); // For 'My Profile' nav functionality

		this.badBrowserCheck(); 
        this.onChangePasswordScreen = window.location.href.includes("code=fromEmail");

		// Remember if nav is fixed
		if (this.navService.isMobile()) { 
			localStorage.setItem("isNavFixed", "false"); 
			this.navService.isNavFixed.set(false);
			this.navService.isNavOpen.set(false);
		} else {
			if (localStorage.getItem("isNavFixed") === "true") {
				this.navService.isNavFixed.set(true);
				this.navService.isNavOpen.set(true);
			} else {
				this.navService.isNavFixed.set(false);
				this.navService.isNavOpen.set(false);
			}			
		}

		if (window.location.href.includes("code=fromEmail")) {
			this.navService.isNavFixed.set(false);
			this.navService.isNavOpen.set(false);
		}
        this.onChangePasswordScreen =  this.gvs.onChangePasswordScreen();
	}

	ngAfterContentChecked() {
		this.isAdmin = this.sec.hasAccess(o => o.adminFeatures);
		this.canManageUsers = this.sec.hasAccess(o => o.manageSiteUsers);
		this.isOrganizationAdmin = this.sec.hasAccess(o => o.permissionProfile == PermissionProfile.OrganizationAdmin);
		this.isSiteUser = this.sec.hasAccess(o => o.permissionProfile == PermissionProfile.SiteUser);
		this.isSiteManager = this.sec.hasAccess(o => o.permissionProfile == PermissionProfile.SiteManager);
	}

	ngDoCheck() { this.isLoggedIn.set(this.sec.isLoggedOn()); }

	updateCheck(): void {
		if (this.swUpdate.isEnabled) {
			this.swUpdate.checkForUpdate()
				.then(() => this.updateCheckText = 'resolved')
				.catch(err => this.updateCheckText = `rejected: ${err.message}`);
			this.swUpdate.versionUpdates.subscribe(() => {
                /* if(confirm("New version available. Load New Version?")) {
                    window.location.reload();
                } */
            });
		}
	}

	/** Set default ng-bootstrap modal options */
	configureModal() {
		// Disable ng bootstrap modal animations by default
		this.modalConfig.animation = false;
		this.modalConfig.ariaLabelledBy = 'modal-basic-title';
	}

	/** Configure rollbar throughout the website */
	configureRollbar() {
		this.rollbar.configure({
			// Set initial user on load (updated in subscription below)
			payload: this.getRollbarPayload(this.sec.getUser())
			, checkIgnore: (uncaught, args, item) => {
				// Prevent duplicate message/URL combinations for the session
				// Don't want to flood rollbar with errors if Angular starts looping
				// an error message.
				const theItem = item as any;
				const errorKey = (theItem?.body?.message?.body ?? '') 
					+ (theItem?.body?.message?.extra?.url?.split('?')[0] ?? '') 
					+ window.location.href;
				// Do not report these errors as they are results of intented system behavior.
				if (
					this.errorCache[errorKey] 
					|| this.rollbarIgnoredCodes.includes(theItem?.body?.message?.extra?.status)
				) { return true; }

				this.errorCache[errorKey] = true;

				// Prevent errors in local development
				if (pcgSettings.mode === 'Development') { return true; }

				return false;
			}
		});

		// Update user if it changes
		this.sec.user$.subscribe(newUser => { this.rollbar.configure({ payload: this.getRollbarPayload(newUser) }); });
	}

	/**
	 * Creates a rollbar payload with user added
	 * 
	 * @param user The user to add
	 */
	getRollbarPayload(user: UserJsVm) {
		return {
			environment: pcgSettings.mode
			, person: !user
				? undefined
				: {
					id: user.id.toString()
					, username: user.userName
					, email: user.email
				  }
			, client: {
				javascript: {
					source_map_enabled: true // true by default
					, code_version: '950ecb1' // Add this into your configuration
					, guess_uncaught_frames: true
				}
			}
		};
	}

	/** Sets the is-logged-in HTML class, which is used to hide the release notes links */
	setIsLoggedInHtmlClass() {
		// Add / remove is-logged-in class
		this.sec.user$.subscribe(user => {
			const isLoggedIn = user 
				? true 
				: false;
			if (isLoggedIn) { this.elRef.nativeElement.classList.add('is-logged-in'); } 
			else { this.elRef.nativeElement.classList.remove('is-logged-in'); }
		});
	}

	myBrowser() {
		if (
			(
				navigator.userAgent.indexOf('Opera') 
				|| navigator.userAgent.indexOf('OPR')
			) !== -1
		) { return 'Opera'; } 
		else if (navigator.userAgent.indexOf('Chrome') !== -1) { return 'Chrome'; } 
		else if (navigator.userAgent.indexOf('Safari') !== -1) { return 'Safari'; } 
		else if (navigator.userAgent.indexOf('Firefox') !== -1) { return 'Firefox'; } 
		else if (
			navigator.userAgent.indexOf('MSIE') !== -1 
			|| navigator.userAgent.indexOf('trident') !== -1
		) { return 'IE'; } 
		else { return 'unknown'; }
	}

	getBrowserVersion() {
		const userAgent = navigator.userAgent;
		let tem: any[] | null;
		let matchTest = userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

		if (/trident/i.test(matchTest[1])) {
			tem = /\brv[ :]+(\d+)/g.exec(userAgent) || [];
			return 'IE ' + (tem[1] || '');
		}
		if (matchTest[1] === 'Chrome') {
			tem = userAgent.match(/\b(OPR|Edge)\/(\d+)/)!;
			if (tem != null) return tem.slice(1).join(' ').replace('OPR', 'Opera');
		}
		matchTest = matchTest[2] 
			? [matchTest[1], matchTest[2]] 
			: [navigator.appName, navigator.appVersion, '-?'];
		if ((tem = userAgent?.match(/version\/(\d+)/i)) != null) matchTest.splice(1, 1, tem[1]);
		return matchTest.join(' ');
	}

	badBrowserCheck() {
		if (
			navigator.userAgent.indexOf('MSIE') !== -1 
			|| navigator.userAgent.indexOf('trident') !== -1
		) {
			this.ms.setSystemMessage(
				'Internet Explorer (IE) is an unsupported browser. Please use a supported browser.', 'error'
			);
		}
	}

    takeToHome() { this.router.navigateByUrl('/dashboard'); }
	toReleaseNotes() { this.router.navigateByUrl("release-notes"); }
    takeToMyProfile() { this.router.navigateByUrl('/users/edit/details/' + this.user?.id); }
    openHelp() { this.router.navigateByUrl("help"); }
    takeToOrgs() { this.router.navigateByUrl('/organizations/list'); }

    // #region Change Event
	openChangeEventModal() { 
        this.httpClient.get(`api/Select/Events`).subscribe((items: any) => { this.eventItems = items; }); 
        this.gvs.currentEventId.set(this.user?.currentEventId);
		this.currentEventId = this.gvs.currentEventId();
        this.model = SwapEventVm.GetModel(this.formGroup);
        this.formGroup?.controls?.eventId?.patchValue(this.user?.currentEventId);
        this.modalService.open(this.modalChangeEvent, { beforeDismiss: () => { return true; } });
    }

    changeEvent(val: number) {
        this.currentEventId = val;
        this.model.eventId = this.currentEventId;
        this.formGroup?.controls?.eventId?.patchValue(this.currentEventId);
    }

    saveChangeEvent(modal: { dismiss: () => void; }) {
        this.httpClient.post('api/SetEvent', this.formGroup.getRawValue())
            .subscribe(async (sm: any) => {
                if (sm.isSuccessful) { 		
                    var newUserInfo: UserJsVm = sm.value;	
                    this.sec.setSecurity(undefined!, newUserInfo, undefined!);
                    this.eventName = newUserInfo.currentEventName!;
                    this.gvs.currentEventName.set(newUserInfo.currentEventName!);
                    if (
                        this.eventName.length > 20
                        && this.eventName.includes(" ")
                    ) { this.eventName = this.eventName.split(" ")[0]; }
                    this.currentEventId = newUserInfo.currentEventId;
                    this.gvs.currentEventId.set(newUserInfo.currentEventId);
                    
                    if (newUserInfo?.eventMous?.length! > 0) {
                        var eventMous: any = newUserInfo?.eventMous?.filter(o => o.eventId == newUserInfo?.currentEventId);
                        if (eventMous[0]?.hasAcknowledgedMOU !== true) { this.openAck(); }
                    }

                    if (this.router.url === '/dashboard') { window.location.reload(); }
                    else { this.router.navigateByUrl(`dashboard`); }
                    modal.dismiss();	
                } 
            }
        );
    }
    
    openAck() {
        this.isAckOpen = true;
        this.modalService.open(MouAckComponent, { backdrop: 'static', scrollable: true });
    }
	// #endregion

	ngOnDestroy(): void { 
		this.isAdmin = false;
		this.canManageUsers = false;
		this.isSiteUser = false;
		this.isSiteManager = false;
	}
}
